import * as React from 'react';

import { useAnnouncements } from '@/api/use-announcements-admin';
import { Panel, StatusDot } from '@/components/ui/panel';
import { formatDateTime } from '@/routes/admin/communication/announcements-format';

// =============================================================================
// announcements-dashboard-alert.tsx — Alerte d'INFORMATION « annonce annulée » (R15-COM-03, DBU l.1087/1245).
// =============================================================================
// Une annonce PLANIFIÉE dont la campagne cible devient inéligible (Clôturée/Abandonnée/Archivée) avant
// l'échéance est AUTO-ANNULÉE (état Annulee, invariant §3.8.5-5). Cet encart READ-SIDE, monté sur le tableau
// de bord admin, signale ces annulations au NIVEAU INFORMATION (DS) — non intrusif, jamais bloquant.
//
// LIMITE assumée : le DTO d'annonce ne porte PAS de discriminant « auto vs manuel » (aucune colonne de motif
// d'annulation en base — hors périmètre DEC-35). L'encart surface donc les annonces `Annulee` du registre ;
// le libellé reste honnête (« annulée », sans inventer la cause). N'affiche RIEN s'il n'y a aucune annulation.
//
// A11y : liste role="status" (annonce polie, non intrusive) ; le niveau INFORMATION est doublé d'un LIBELLÉ
// TEXTE sr-only (la pastille n'est qu'un renfort, WCAG 1.4.1). Gabarit <Panel> commun au tableau de bord. Autorité = API : l'UI lit et habille.

/** Nombre maximum d'annonces annulées listées dans l'encart (les plus récentes d'abord). */
const MAX_LISTED = 5;

export function AnnouncementsDashboardAlert(): React.JSX.Element | null {
  const cancelled = useAnnouncements({ state: 'Annulee' });
  const items = cancelled.data ?? [];

  // Rien à signaler (chargement / erreur / aucune annulation) : l'encart ne se matérialise pas.
  if (items.length === 0) {
    return null;
  }

  // Les plus récentes d'abord (par date de planification manquée, puis identifiant pour un ordre stable).
  const sorted = [...items].sort((a, b) => {
    const aKey = a.scheduledFor ?? '';
    const bKey = b.scheduledFor ?? '';
    if (aKey !== bKey) {
      return aKey < bKey ? 1 : -1;
    }
    return a.announcementId.localeCompare(b.announcementId);
  });
  const listed = sorted.slice(0, MAX_LISTED);

  return (
    <Panel
      headingId="cancelled-announcements-heading"
      title="Annonces annulées"
      count={items.length}
      aside="Campagne cible devenue inéligible avant l'envoi · non diffusées"
    >
      <ul role="status">
        {listed.map((announcement) => (
          <li
            key={announcement.announcementId}
            className="flex items-center gap-3 border-b border-border px-6 py-3.5 text-sm last:border-b-0"
          >
            <StatusDot className="bg-muted-foreground" />
            <span className="min-w-0 flex-1">
              {/* Niveau INFORMATION doublé d'un libellé texte (WCAG : jamais couleur seule). */}
              <span className="sr-only">Information : </span>
              <span className="font-medium">{announcement.titre}</span>{' '}
              <span className="text-muted-foreground">
                · campagne {announcement.campaignIntitule ?? announcement.campaignId}
                {announcement.scheduledFor !== null
                  ? ` · planifiée pour le ${formatDateTime(announcement.scheduledFor)}`
                  : ''}
              </span>
            </span>
          </li>
        ))}
      </ul>
      {items.length > listed.length ? (
        <p className="border-t border-border px-6 py-3 text-sm text-muted-foreground">
          … et {items.length - listed.length} autre(s). Consultez le registre des annonces
          pour le détail.
        </p>
      ) : null}
    </Panel>
  );
}
